import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Checkbox,
  Radio,
  Autocomplete,
  Card as RoleCard,
  IconButton,
  Paper,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  Grid,
  Slider,
} from "@mui/material";
import { Chip, Divider } from "@mui/material";
import { CheckCircle, Language, Delete } from "@mui/icons-material";
import { Alert, Stack } from "@mui/material";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  NavigateNext as NavigateNextIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useProfile } from "../../../contexts/ProfileContext";
import { styled } from "@mui/material/styles";
import Cookies from "js-cookie";
import AddSkillsDialog from "./AddSkillsDialog";
import axiosInstance from "../../../axios/axiosInstance";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const DeleteButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  "&:hover": {
    backgroundColor: theme.palette.error.dark,
  },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.grey[400],
    color: theme.palette.grey[600],
  },
}));

const initialSkillsList = [
  "Financial Modeling",
  "General Ledger Management",
  "Business Diagnosis",
  "Data Analysis",
  "Project Management",
  "Strategic Planning",
];

const levels = ["Beginner", "Intermediate", "Advanced", "Expert"];
const fluencyLevels = [
  "Basic",
  "Intermediate",
  "Business fluent",
  "Bilingual/Native",
];
const languages = [
  { name: "English" },
  { name: "French" },
  { name: "German" },
  { name: "Italian" },
  { name: "Spanish" },
  { name: "Dutch" },
  { name: "Portuguese" },
  { name: "Polish" },
  { name: "Czech" },
  { name: "Danish" },
  { name: "Swedish" },
  { name: "Finnish" },
  { name: "Norwegian" },
  { name: "Icelandic" },
  { name: "Romanian" },
  { name: "Hungarian" },
  { name: "Greek" },
  { name: "Slovak" },
  { name: "Slovenian" },
  { name: "Croatian" },
  { name: "Bulgarian" },
  { name: "Estonian" },
  { name: "Latvian" },
  { name: "Lithuanian" },
  { name: "Maltese" },
  { name: "Irish" },
  { name: "Romansh" },
];

const PreferredRoles = () => {
  const navigate = useNavigate();
  const {
    updateTempPreferredRoles,
    tempProfileData,
    isProfileReadOnly,
    profile,
    isEditingProfile,
    setIsEditingProfile,
    hasProfileChanges,
    setHasProfileChanges,
    hasExistingProfile,
    showFullProfileFields,
  } = useProfile();

  const [roles, setRoles] = useState([
    {
      id: 1,
      function: "",
      role: "",
      employmentType: "Full-time",
      locationType: "On-site",
      willRelocate: "No",
      workSetup: [],
      skills: [],
    },
  ]);

  // Universal fields (not role-specific)
  const [softSkills, setSoftSkills] = useState([]);
  const [languagesList, setLanguagesList] = useState([]);
  const [softSkillList, setSoftSkillList] = useState([]);
  const [loadingSoftSkills, setLoadingSoftSkills] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [selectedFluency, setSelectedFluency] = useState("");

  const [localFunctions, setLocalFunctions] = useState([]);
  const [roleOptions, setRoleOptions] = useState({});
  const [loadingRoles, setLoadingRoles] = useState({});
  const [skillsList, setSkillsList] = useState(initialSkillsList);
  const [specificSkillsMap, setSpecificSkillsMap] = useState({});
  const [loadingSkill, setLoadingSkill] = useState(false);
  const [openRoleId, setOpenRoleId] = useState(null);
  const [errors, setErrors] = useState([]);
  const [rolesSkills, setRolesSkills] = useState({});
  const [skills, setSkills] = useState([
    { id: Date.now(), skillCategory: "", subSkill: "", level: 2 },
  ]);

  const token = Cookies.get("token");

  // Fetch soft skills
  const fetchSoftSkills = async () => {
    try {
      setLoadingSoftSkills(true);
      const res = await axiosInstance.get(`api/job-postings/soft-skills/list`);
      setSoftSkillList(res.data || []);
    } catch (error) {
      console.error("Error fetching soft skills:", error);
    } finally {
      setLoadingSoftSkills(false);
    }
  };

  useEffect(() => {
    fetchSoftSkills();
  }, []);

  const validateRoles = (roles, softSkills, languages) => {
    const newErrors = [];

    roles.forEach((e) => {
      if (!e.function || !e.role) {
        newErrors.push("Function and role are required");
      }
      if (e.function && e.role && (!e.skills || e.skills.length === 0)) {
        newErrors.push(
          `Skills are empty for role: ${
            roleOptions[e.role] &&
            roleOptions[e.role].find((r) => r.id === e.role)
              ? roleOptions[e.role].find((r) => r.id === e.role).name
              : null
          }`,
        );
      }
    });

    // Validate soft skills and languages only when those fields are visible
    if (showFullProfileFields) {
      if (!softSkills || softSkills.length === 0) {
        newErrors.push("Add at least one soft skill");
      } else {
        const hasEmptyEvidence = softSkills.some(
          (s) => !s.evidence || s.evidence.trim() === "",
        );
        if (hasEmptyEvidence) {
          newErrors.push("Example cannot be empty for any soft skill");
        }
      }

      // Validate languages
      if (!languages || languages.length === 0) {
        newErrors.push("Add at least one language");
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const fetchSkills = async (functionId, roleId) => {
    try {
      setLoadingSkill(true);
      const res = await axiosInstance.get(
        `/api/profile/job-role-skills/${functionId}/${roleId}`,
      );

      if (res.data) {
        setSkillsList(
          Array.isArray(res.data.specific_skills) ? res.data.skills : [],
        );
        setSpecificSkillsMap(
          typeof res.data.specific_skills === "object"
            ? res.data.specific_skills
            : {},
        );

        // ✅ Add specificSkillsMap data to rolesSkills using roleId as key
        setRolesSkills((prev) => ({
          ...prev,
          [roleId]:
            typeof res.data.specific_skills === "object"
              ? res.data.specific_skills
              : {},
        }));
      } else {
        setSkillsList([]);
        setSpecificSkillsMap({});
      }

      setLoadingSkill(false);
    } catch (err) {
      console.error("Error fetching skills:", err);
      setLoadingSkill(false);
      setSkillsList([]);
      setSpecificSkillsMap({});
    }
  };

  const handleOpen = (functionId, roleId, userJobRoleId) => {
    fetchSkills(functionId, roleId);
    setOpenRoleId(userJobRoleId);
    const currentRole = roles.find((r) => r.id === userJobRoleId);
    if (currentRole?.skills && currentRole.skills.length > 0) {
      setSkills(
        currentRole.skills.map((skill) => ({
          ...skill,
          id: skill.id || Date.now() + Math.random(),
        })),
      );
    } else {
      setSkills([]);
    }
  };

  const handleClose = () => {
    setOpenRoleId(null);
    setSkills([]);
  };

  const handleSave = (skillsData) => {
    console.log("Saving skills data:", skillsData);
    setRoles((prev) =>
      prev.map((r) => (r.id === openRoleId ? { ...r, skills: skillsData } : r)),
    );

    if (isEditingProfile) {
      hasLocalEdits.current = true;
      setHasProfileChanges(true); // Ensure context updates
    }
    handleClose();
  };

  const handleDeselectSkill = (roleId, skillName) => {
    const role = roles.find((r) => r.id === roleId);
    if (!role) return;

    if (role.skills.length <= 1) {
      toast.warn("At least one technical skill is required for this role.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    const updatedSkills = role.skills.filter(
      (s) => Object.keys(s)[0] !== skillName,
    );
    updateRole(roleId, "skills", updatedSkills);
  };

  // Track local edits on this page to prevent useEffect from overwriting them
  const hasLocalEdits = useRef(false);

  useEffect(() => {
    // Don't overwrite local form state while user has made edits on this page
    if (isEditingProfile && hasLocalEdits.current) return;

    let dataSource = isProfileReadOnly
      ? profile?.preferredRoles
      : tempProfileData.preferredRoles?.roles || tempProfileData.preferredRoles;
    let dataSoftSkills = isProfileReadOnly
      ? profile?.softSkills
      : tempProfileData.preferredRoles?.softSkills ||
        tempProfileData.softSkills;
    let dataLanguages = isProfileReadOnly
      ? profile?.languages
      : tempProfileData.preferredRoles?.languages || tempProfileData.languages;

    if (!dataSource || dataSource.length === 0) {
      try {
        const saved = localStorage.getItem("tempProfileData");
        if (saved) {
          const parsed = JSON.parse(saved);
          dataSource = parsed.preferredRoles?.roles || [];

          // Load soft skills and languages from temp data
          if (parsed.softSkills) setSoftSkills(parsed.softSkills);
          if (parsed.languages) setLanguagesList(parsed.languages);
        }
      } catch (error) {
        console.error("Error loading from localStorage:", error);
      }
    }

    if (dataSoftSkills && dataSoftSkills.length > 0) {
      setSoftSkills(dataSoftSkills);
    }

    if (dataLanguages && dataLanguages.length > 0) {
      setLanguagesList(dataLanguages);
    }

    if (dataSource && dataSource.length > 0) {
      setRoles(dataSource);
      dataSource.forEach((role) => {
        if (role.function) {
          fetchRolesForFunction(role.function, role.id);
        }
        if (role.function && role.role) {
          fetchSkills(role.function, role.role);
        }
      });
    } else if (dataSource === null || dataSource === undefined) {
      setRoles([
        {
          id: 1,
          function: "",
          role: "",
          locationType: "On-site",
          skills: [],
        },
      ]);
    }
  }, [profile, tempProfileData.preferredRoles, isProfileReadOnly]);

  const fetchRolesForFunction = async (functionId, roleCardId) => {
    setLoadingRoles((prev) => ({ ...prev, [roleCardId]: true }));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/profile/job-roles/${functionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) throw new Error("Failed to fetch job roles");
      const data = await response.json();
      setRoleOptions((prev) => ({ ...prev, [roleCardId]: data }));
    } catch (error) {
      console.error("Error fetching roles:", error);
    } finally {
      setLoadingRoles((prev) => ({ ...prev, [roleCardId]: false }));
    }
  };

  useEffect(() => {
    const fetchJobFunctions = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/profile/job-functions`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (!response.ok) throw new Error("Failed to fetch job functions");
        const data = await response.json();
        setLocalFunctions(data);
      } catch (error) {
        console.error("Error fetching functions:", error);
      }
    };

    fetchJobFunctions();
  }, []);

  const addRole = () => {
    const newId = Math.max(...roles.map((r) => r.id), 0) + 1;
    setRoles((prev) => [
      ...prev,
      {
        id: newId,
        function: "",
        role: "",
        locationType: "On-site",
        skills: [],
      },
    ]);
    if (isEditingProfile) {
      hasLocalEdits.current = true;
      setHasProfileChanges(true);
    }
  };

  const deleteRole = (id) => {
    setRoles((prev) => prev.filter((r) => r.id !== id));
    if (isEditingProfile) {
      hasLocalEdits.current = true;
      setHasProfileChanges(true);
    }
  };

  const updateRole = (id, field, value) => {
    setRoles((prev) => {
      const updated = prev.map((r) =>
        r.id === id ? { ...r, [field]: value } : r,
      );
      return updated;
    });

    // Mark as changed when editing
    if (isEditingProfile) {
      hasLocalEdits.current = true;
      setHasProfileChanges(true);
    }
  };

  const handleContinue = async () => {
    try {
      setErrors([]);
      const isValid = validateRoles(roles, softSkills, languagesList);

      if (!isValid) {
        return;
      }

      // Save roles, soft skills, and languages to temporary storage
      await updateTempPreferredRoles({
        roles,
        softSkills,
        languages: languagesList,
      });

      navigate("/candidate/profile/review");
    } catch (error) {
      console.error("Error saving preferred roles:", error);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, width: "100%", mx: "auto", mt: 0 }}>
      <ToastContainer />
      {/* Header with Edit Button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Pick preferred roles & Skills
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Select one or more roles you want to be searchable for.
          </Typography>
        </Box>

        {/* Show Edit Profile button only for users with existing profiles */}
        {hasExistingProfile && !isProfileReadOnly && !isEditingProfile && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => setIsEditingProfile(true)}
            sx={{ minWidth: 160 }}
          >
            Edit Profile
          </Button>
        )}
      </Box>
      {errors.length > 0 && (
        <Stack spacing={1} sx={{ mb: 3 }}>
          {errors.map((err, idx) => (
            <Alert key={idx} severity="error">
              {err}
            </Alert>
          ))}
        </Stack>
      )}

      {showFullProfileFields && (
      <>
      {/* Universal Soft Skills Section */}
      <Box sx={{ mt: 4 }}>
        <Grid container spacing={3} padding={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Soft Skills *
            </Typography>

            {loadingSoftSkills ? (
              <TextField
                disabled
                placeholder="Loading SoftSkills..."
                fullWidth
              />
            ) : (
              <Autocomplete
                multiple
                options={softSkillList || []}
                disableCloseOnSelect
                getOptionLabel={(option) =>
                  option.name || option.softSkill || ""
                }
                value={
                  softSkillList
                    ? softSkillList.filter((skill) =>
                        softSkills.some((s) => s.id === skill.id),
                      )
                    : []
                }
                onChange={(event, newValue) => {
                  if (isEditingProfile) {
                    hasLocalEdits.current = true;
                    setHasProfileChanges(true);
                  }
                  setSoftSkills(
                    newValue.map((skill) => {
                      const existing = softSkills.find(
                        (s) => s.id === skill.id,
                      );
                      return (
                        existing || {
                          id: skill.id || skill.softSkillId,
                          softSkill: skill.name,
                          description: skill.description,
                          evidence: "",
                        }
                      );
                    }),
                  );
                }}
                renderOption={(props, option) => (
                  <Tooltip
                    title={option.description || ""}
                    placement="right"
                    arrow
                  >
                    <li {...props}>{option.name}</li>
                  </Tooltip>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select soft skills"
                    InputLabelProps={{ shrink: true }}
                    disabled={isProfileReadOnly || !isEditingProfile}
                  />
                )}
                noOptionsText="No soft skills available"
                disabled={isProfileReadOnly || !isEditingProfile}
              />
            )}

            {softSkills && softSkills.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Add Example for Each Skill
                </Typography>
                <Stack spacing={2}>
                  {softSkills.map((skill) => (
                    <Box
                      key={skill.id}
                      p={2}
                      border="1px solid #ccc"
                      borderRadius={2}
                      display="flex"
                      alignItems="center"
                      gap={2}
                    >
                      <Box flex={1}>
                        <Typography variant="body1" fontWeight={600}>
                          {skill.softSkill} *
                        </Typography>
                        <TextField
                          label="Example"
                          placeholder="Provide example for this skill"
                          fullWidth
                          size="small"
                          margin="dense"
                          value={skill.evidence || ""}
                          onChange={(e) => {
                            if (isEditingProfile) {
                              hasLocalEdits.current = true;
                              setHasProfileChanges(true);
                            }
                            const newEvidence = e.target.value;
                            setSoftSkills((prev) =>
                              prev.map((s) =>
                                s.id === skill.id
                                  ? { ...s, evidence: newEvidence }
                                  : s,
                              ),
                            );
                          }}
                          disabled={isProfileReadOnly || !isEditingProfile}
                        />
                      </Box>
                      {!isProfileReadOnly && (
                        <IconButton
                          color="error"
                          disabled={isProfileReadOnly || !isEditingProfile}
                          onClick={() => {
                            if (isEditingProfile) {
                              hasLocalEdits.current = true;
                              setHasProfileChanges(true);
                            }
                            setSoftSkills((prev) =>
                              prev.filter((s) => s.id !== skill.id),
                            );
                          }}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Universal Languages Section */}
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={3} padding={3}>
          <Grid item xs={12}>
            <Autocomplete
              options={languages}
              value={selectedLanguage}
              onChange={(event, newValue) => {
                setSelectedLanguage(newValue);
              }}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) =>
                option.name === value.name
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Language *"
                  placeholder="Add language"
                  InputLabelProps={{ shrink: true }}
                />
              )}
              disabled={isProfileReadOnly || !isEditingProfile}
            />

            {selectedLanguage && (
              <Box
                sx={{ mt: 2, display: "flex", gap: 2, alignItems: "center" }}
              >
                <FormControl fullWidth size="small">
                  <InputLabel>Fluency</InputLabel>
                  <Select
                    label="Fluency"
                    value={selectedFluency}
                    onChange={(e) => setSelectedFluency(e.target.value)}
                    disabled={isProfileReadOnly || !isEditingProfile}
                  >
                    {fluencyLevels.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  onClick={() => {
                    if (isEditingProfile) {
                      hasLocalEdits.current = true;
                      setHasProfileChanges(true);
                    }
                    if (selectedLanguage && selectedFluency) {
                      const isAlreadyAdded = languagesList.some(
                        (lang) => lang.name === selectedLanguage.name,
                      );

                      if (!isAlreadyAdded) {
                        setLanguagesList((prev) => [
                          ...prev,
                          {
                            name: selectedLanguage.name,
                            fluency: selectedFluency,
                          },
                        ]);
                      }

                      setSelectedLanguage(null);
                      setSelectedFluency("");
                    }
                  }}
                  disabled={isProfileReadOnly || !isEditingProfile}
                >
                  Add
                </Button>
              </Box>
            )}

            {languagesList.length > 0 && (
              <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {languagesList.map((lang, index) => (
                  <Chip
                    key={index}
                    label={`${lang.name} (${lang.fluency})`}
                    onDelete={
                      isProfileReadOnly || !isEditingProfile
                        ? undefined
                        : () => {
                            hasLocalEdits.current = true;
                            setHasProfileChanges(true);

                            setLanguagesList((prev) =>
                              prev.filter((_, i) => i !== index),
                            );
                          }
                    }
                  />
                ))}
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
      <Divider sx={{ my: 4 }} />
      </>
      )}

      <Box sx={{ mt: 4 }}>
        {roles.map((role, index) => (
          <>
            <Box
              key={role.id}
              elevation={2}
              sx={{ p: 3, mb: 3, position: "relative" }}
            >
              <DeleteButton
                className="delete-button"
                onClick={() => deleteRole(role.id)}
                disabled={
                  roles.length === 1 || isProfileReadOnly || !isEditingProfile
                }
              >
                <DeleteIcon />
              </DeleteButton>

              <Typography variant="h6" gutterBottom>
                Preferred Role
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Select function and role that you want to be searchable for.
              </Typography>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Functions *
                </Typography>
                {localFunctions.length > 0 ? (
                  <Autocomplete
                    options={localFunctions}
                    disabled={isProfileReadOnly || !isEditingProfile}
                    getOptionLabel={(option) => option.name || ""}
                    value={
                      localFunctions.find((f) => f.id === role.function) || null
                    }
                    onChange={(_, newValue) => {
                      const newFunction = newValue ? Number(newValue.id) : "";
                      if (newFunction === role.function) return;
                      // Role and skills depend on the function, so reset them
                      updateRole(role.id, "function", newFunction);
                      updateRole(role.id, "role", "");
                      updateRole(role.id, "skills", []);
                      setRoleOptions((prev) => ({ ...prev, [role.id]: [] }));
                      if (newValue && newValue.id)
                        fetchRolesForFunction(newValue.id, role.id);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select function"
                        disabled={isProfileReadOnly || !isEditingProfile}
                      />
                    )}
                  />
                ) : (
                  <TextField disabled placeholder="Loading functions..." />
                )}
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Role *
                </Typography>
                <Autocomplete
                  options={roleOptions[role.id] || []}
                  getOptionLabel={(option) => option.name || ""}
                  value={
                    roleOptions[role.id] &&
                    roleOptions[role.id].find((r) => r.id === role.role)
                      ? roleOptions[role.id].find((r) => r.id === role.role)
                      : null
                  }
                  onChange={(_, newValue) => {
                    const newRole = newValue ? Number(newValue.id) : "";
                    if (newRole === role.role) return;
                    // Skills are role-specific, so reset them on role change
                    updateRole(role.id, "role", newRole);
                    updateRole(role.id, "skills", []);
                  }}
                  disabled={
                    !role.function ||
                    loadingRoles[role.id] ||
                    isProfileReadOnly ||
                    !isEditingProfile
                  }
                  loading={loadingRoles[role.id]}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select role"
                      disabled={isProfileReadOnly || !isEditingProfile}
                    />
                  )}
                />
              </Box>

              {role.skills && role.skills.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    mt: 3,
                    p: 2.5,
                    backgroundColor: "#ffffff",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: "#1a237e",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: 4,
                        height: 20,
                        backgroundColor: "#1976d2",
                        borderRadius: 2,
                      }}
                    />
                    Added Skills ({role.skills.length})
                  </Typography>
                  {[...role.skills]
                    .sort((a, b) => {
                      const nameA = Object.keys(a)[0].toLowerCase();
                      const nameB = Object.keys(b)[0].toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map((mainSkill, index) => {
                      const skillName = Object.keys(mainSkill)[0];
                      const skillData = mainSkill[skillName];

                      return (
                        <Box
                          key={index}
                          sx={{
                            borderBottom:
                              index < role.skills.length - 1
                                ? "1px solid #f0f0f0"
                                : "none",
                            py: 2,
                            padding: 2,
                          }}
                        >
                          {/* MAIN SKILL NAME */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              mb: 1,
                            }}
                          >
                            <Checkbox
                              disabled={isProfileReadOnly || !isEditingProfile}
                              checked={true}
                              onChange={() =>
                                handleDeselectSkill(role.id, skillName)
                              }
                              size="small"
                              sx={{
                                color: "#6c5ce7",
                                "&.Mui-checked": {
                                  color: "#6c5ce7",
                                },
                                p: 0,
                              }}
                            />
                            <Typography
                              sx={{ fontWeight: 800, color: "#333", mb: 0 }}
                            >
                              {skillName}
                            </Typography>
                          </Box>

                          {/* MAIN SKILL LEVEL SLIDER */}
                          <Box sx={{ mt: 2, px: 1 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              Skill Level
                            </Typography>
                            <Slider
                              value={levels.findIndex(
                                (l) =>
                                  l.toLowerCase() ===
                                  skillData.level?.toLowerCase(),
                              )}
                              min={0}
                              max={levels.length - 1}
                              step={1}
                              marks={levels.map((lvl, i) => ({
                                value: i,
                                label: lvl,
                              }))}
                              onChange={(_, newValue) => {
                                const updatedSkills = [...role.skills];
                                const skillIndex = updatedSkills.findIndex(
                                  (s) => Object.keys(s)[0] === skillName,
                                );
                                if (skillIndex !== -1) {
                                  updatedSkills[skillIndex][skillName].level =
                                    levels[newValue];
                                  updateRole(role.id, "skills", updatedSkills);
                                }
                              }}
                              disabled={isProfileReadOnly || !isEditingProfile}
                              sx={{ width: "60%" }}
                            />
                          </Box>

                          {/* SUB-SKILLS CHECKBOXES */}
                          {rolesSkills &&
                            rolesSkills[role.role] &&
                            rolesSkills[role.role][skillName] && (
                              <Box sx={{ mt: 2, pl: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 1, fontWeight: 500 }}
                                >
                                  Sub-skills
                                </Typography>

                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 2,
                                  }}
                                >
                                  {[...rolesSkills[role.role][skillName]]
                                    .filter(
                                      (subSkill) =>
                                        subSkill.trim().toLowerCase() !==
                                        skillName.trim().toLowerCase(),
                                    )
                                    .sort((a, b) => a.localeCompare(b)) // sort sub-skills alphabetically
                                    .map((subSkill, subIndex) => {
                                      const subSkillData = (
                                        skillData.subSkills || []
                                      ).find((s) => s.name === subSkill);
                                      const subSelected = !!subSkillData;
                                      const subLevelIndex = levels.findIndex(
                                        (l) =>
                                          l.toLowerCase() ===
                                          subSkillData?.level?.toLowerCase(),
                                      );
                                      const subSliderValue =
                                        subLevelIndex !== -1
                                          ? subLevelIndex
                                          : 1;

                                      return (
                                        <Box
                                          key={subIndex}
                                          sx={{
                                            width: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            mb: 1,
                                          }}
                                        >
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                checked={subSelected}
                                                onChange={(e) => {
                                                  const updatedSkills = [
                                                    ...role.skills,
                                                  ];
                                                  const skillIndex =
                                                    updatedSkills.findIndex(
                                                      (s) =>
                                                        Object.keys(s)[0] ===
                                                        skillName,
                                                    );
                                                  if (skillIndex !== -1) {
                                                    const currentSubSkills =
                                                      updatedSkills[skillIndex][
                                                        skillName
                                                      ].subSkills || [];

                                                    let updatedSubSkills;
                                                    if (e.target.checked) {
                                                      updatedSubSkills = [
                                                        ...currentSubSkills,
                                                        {
                                                          name: subSkill,
                                                          level: "Intermediate",
                                                        },
                                                      ];
                                                    } else {
                                                      updatedSubSkills =
                                                        currentSubSkills.filter(
                                                          (s) =>
                                                            s.name !== subSkill,
                                                        );
                                                    }

                                                    updatedSkills[skillIndex][
                                                      skillName
                                                    ].subSkills =
                                                      updatedSubSkills;
                                                    updateRole(
                                                      role.id,
                                                      "skills",
                                                      updatedSkills,
                                                    );
                                                  }
                                                }}
                                                disabled={
                                                  isProfileReadOnly ||
                                                  !isEditingProfile
                                                }
                                              />
                                            }
                                            label={subSkill}
                                          />
                                          {subSelected && (
                                            <Box sx={{ ml: 4, mt: 0.5 }}>
                                              <Slider
                                                value={subSliderValue}
                                                min={0}
                                                max={levels.length - 1}
                                                step={1}
                                                marks={levels.map((lvl, i) => ({
                                                  value: i,
                                                  label: lvl,
                                                }))}
                                                onChange={(_, newValue) => {
                                                  const updatedSkills = [
                                                    ...role.skills,
                                                  ];
                                                  const skillIndex =
                                                    updatedSkills.findIndex(
                                                      (s) =>
                                                        Object.keys(s)[0] ===
                                                        skillName,
                                                    );
                                                  if (skillIndex !== -1) {
                                                    const updatedSubSkills =
                                                      updatedSkills[skillIndex][
                                                        skillName
                                                      ].subSkills.map((s) =>
                                                        s.name === subSkill
                                                          ? {
                                                              ...s,
                                                              level:
                                                                levels[
                                                                  newValue
                                                                ],
                                                            }
                                                          : s,
                                                      );
                                                    updatedSkills[skillIndex][
                                                      skillName
                                                    ].subSkills =
                                                      updatedSubSkills;
                                                    updateRole(
                                                      role.id,
                                                      "skills",
                                                      updatedSkills,
                                                    );
                                                  }
                                                }}
                                                disabled={
                                                  isProfileReadOnly ||
                                                  !isEditingProfile
                                                }
                                                sx={{
                                                  width: "40%",
                                                  "& .MuiSlider-markLabel": {
                                                    fontSize: "0.7rem",
                                                  },
                                                }}
                                              />
                                            </Box>
                                          )}
                                        </Box>
                                      );
                                    })}
                                </Box>
                              </Box>
                            )}
                        </Box>
                      );
                    })}
                </Paper>
              )}

              {role.function && role.role && (
                <Box sx={{ mt: 3 }}>
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    onClick={() =>
                      handleOpen(role.function, role.role, role.id)
                    }
                    disabled={isProfileReadOnly || !isEditingProfile}
                  >
                    {role.skills && role.skills.length > 0
                      ? "Edit Technical Skills *"
                      : "Add Technical Skills *"}
                  </Button>
                </Box>
              )}
            </Box>

            <AddSkillsDialog
              skillsList={specificSkillsMap}
              open={Boolean(openRoleId)}
              handleClose={handleClose}
              handleSave={handleSave}
              isLoading={loadingSkill}
              selectedSkillsList={role.skills}
            />
          </>
        ))}

        {/* Only one preferred role per candidate - "Add preferred role" button disabled
        {!isProfileReadOnly && !isEditingProfile && (
          <Button
            startIcon={<AddIcon />}
            disabled={isProfileReadOnly || !isEditingProfile}
            onClick={addRole}
            variant="outlined"
            sx={{ mt: 2, mb: 4 }}
          >
            Add preferred role
          </Button>
        )} */}

        <Divider sx={{ my: 4 }} />

        <Box
          sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}
        >
          {/* Show Cancel and Save buttons only for users with existing profiles */}
          {hasExistingProfile && isEditingProfile && (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setIsEditingProfile(false);
                  // Reload data from profile/tempProfileData
                  const dataSource = isProfileReadOnly
                    ? profile?.preferredRoles
                    : tempProfileData.preferredRoles?.roles ||
                      tempProfileData.preferredRoles;
                  const dataSoftSkills = isProfileReadOnly
                    ? profile?.softSkills
                    : tempProfileData.preferredRoles?.softSkills ||
                      tempProfileData.softSkills;
                  const dataLanguages = isProfileReadOnly
                    ? profile?.languages
                    : tempProfileData.preferredRoles?.languages ||
                      tempProfileData.languages;

                  if (dataSource && dataSource.length > 0) {
                    setRoles(dataSource);
                  }
                  if (dataSoftSkills && dataSoftSkills.length > 0) {
                    setSoftSkills(dataSoftSkills);
                  }
                  if (dataLanguages && dataLanguages.length > 0) {
                    setLanguagesList(dataLanguages);
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  await handleContinue();
                }}
              >
                Save & Continue
              </Button>
            </>
          )}
          {!hasExistingProfile && (
            <Button
              type="submit"
              variant="contained"
              size="large"
              endIcon={<NavigateNextIcon />}
              onClick={async () => {
                await handleContinue();
              }}
            >
              Continue
            </Button>
          )}
          {!isProfileReadOnly && !isEditingProfile && (
            <Button
              variant="contained"
              size="large"
              endIcon={<NavigateNextIcon />}
              onClick={handleContinue}
            >
              Continue
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default PreferredRoles;
